import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCommentOutlined,
  AutoAwesomeOutlined,
  CloseOutlined,
  DeleteOutline,
  HistoryOutlined,
  SendOutlined,
  StopCircleOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MessageBubble from './MessageBubble';
import ConfirmActionCard from './ConfirmActionCard';
import { hasMinRole } from '../../utils/roles';

const formatWhen = (value, language) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString(language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (error) {
    return '';
  }
};

const AssistantPanel = ({ chat, user, onClose, isMobile }) => {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const {
    messages, streaming, loadingHistory, error, clearError, sendMessage, confirmAction, cancelAction, stop,
    newConversation, loadConversation, deleteConversation, conversations, refreshConversations, conversationId,
  } = chat;

  const suggestions = useMemo(() => {
    const keys = hasMinRole(user, 'admin')
      ? ['pendingQuotes', 'quotesThisMonth', 'todayJobs', 'openTickets', 'leaveRequests']
      : hasMinRole(user, 'sales')
        ? ['pendingQuotes', 'quotesThisMonth', 'findCustomer', 'todayJobs']
        : ['todayJobs', 'myTasks', 'openTickets'];
    return keys.map((key) => t(`assistant.suggestions.${key}`));
  }, [t, user]);

  useEffect(() => {
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!isMobile && inputRef.current) inputRef.current.focus();
  }, [isMobile, conversationId]);

  const submit = () => {
    const text = draft.trim();
    if (!text || streaming) return;
    setDraft('');
    sendMessage(text);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const openHistory = (event) => {
    refreshConversations();
    setMenuAnchor(event.currentTarget);
  };

  return (
    <Box
      role="dialog"
      aria-modal={isMobile ? 'true' : 'false'}
      aria-labelledby="assistant-panel-title"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}
    >
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeOutlined aria-hidden="true" sx={{ color: 'secondary.light' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="assistant-panel-title" variant="subtitle1" component="h2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {t('assistant.title')}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {t('assistant.subtitle')}
          </Typography>
        </Box>
        <Tooltip title={t('assistant.newChat')}>
          <IconButton size="small" color="inherit" onClick={newConversation} aria-label={t('assistant.newChat')} disabled={streaming}>
            <AddCommentOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('assistant.history')}>
          <IconButton size="small" color="inherit" onClick={openHistory} aria-label={t('assistant.history')} aria-haspopup="menu" aria-expanded={Boolean(menuAnchor)}>
            <HistoryOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('assistant.close')}>
          <IconButton size="small" color="inherit" onClick={onClose} aria-label={t('assistant.close')}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 360, width: 320 } } }}
        MenuListProps={{ 'aria-label': t('assistant.history') }}
      >
        {conversations.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary={t('assistant.noHistory')} />
          </MenuItem>
        )}
        {conversations.map((conversation) => (
          <MenuItem
            key={conversation.id}
            selected={conversation.id === conversationId}
            onClick={() => { setMenuAnchor(null); loadConversation(conversation.id); }}
            sx={{ alignItems: 'flex-start' }}
          >
            <ListItemText
              primary={conversation.title || t('assistant.untitled')}
              secondary={formatWhen(conversation.last_message_at || conversation.created_at, i18n.language)}
              primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
            <ListItemIcon sx={{ minWidth: 0, ml: 1 }}>
              <IconButton
                size="small"
                edge="end"
                aria-label={t('assistant.deleteConversationAria', { title: conversation.title || t('assistant.untitled') })}
                onClick={(event) => { event.stopPropagation(); deleteConversation(conversation.id); }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </ListItemIcon>
          </MenuItem>
        ))}
      </Menu>

      {/* Messages */}
      <Box
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-label={t('assistant.messagesAria')}
        sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, bgcolor: '#f6f7fb' }}
      >
        {loadingHistory && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">{t('assistant.loading')}</Typography>
          </Stack>
        )}
        {!loadingHistory && messages.length === 0 && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('assistant.welcomeTitle', { name: user?.first_name || '' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('assistant.welcomeBody')}
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }} role="list" aria-label={t('assistant.suggestionsAria')}>
              {suggestions.map((suggestion) => (
                <Chip
                  key={suggestion}
                  role="listitem"
                  label={suggestion}
                  onClick={() => sendMessage(suggestion)}
                  variant="outlined"
                  color="primary"
                  size="small"
                  aria-label={t('assistant.suggestionAria', { text: suggestion })}
                />
              ))}
            </Stack>
          </Box>
        )}
        {messages.map((message) => (
          message.role === 'action' ? (
            <ConfirmActionCard
              key={message.id}
              action={message.action}
              onConfirm={confirmAction}
              onCancel={cancelAction}
              disabled={streaming}
            />
          ) : (
            <MessageBubble key={message.id} message={message} />
          )
        ))}
      </Box>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ borderRadius: 0 }}>
          {error === 'load_failed' ? t('assistant.errors.loadFailed') : error}
        </Alert>
      )}

      <Divider />
      {/* Composer */}
      <Box component="form" onSubmit={(event) => { event.preventDefault(); submit(); }} sx={{ p: 1, pb: isMobile ? 'max(8px, env(safe-area-inset-bottom))' : 1 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          size="small"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('assistant.placeholder')}
          inputProps={{ 'aria-label': t('assistant.inputAria'), enterKeyHint: 'send' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {streaming ? (
                  <IconButton onClick={stop} aria-label={t('assistant.stop')} color="secondary" edge="end">
                    <StopCircleOutlined />
                  </IconButton>
                ) : (
                  <IconButton type="submit" aria-label={t('assistant.send')} color="primary" edge="end" disabled={!draft.trim()}>
                    <SendOutlined />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
          {t('assistant.disclaimer')}
        </Typography>
      </Box>
    </Box>
  );
};

export default AssistantPanel;
